// #294 (Phase B core) — the open critical events and what they drive on every page: the alert bar, the
// browser tab title, and the critical sound with its repeat. One query (?critical=open) feeds all of them
// and the dashboard's pinned rows; the WebSocket refreshes it (a critical event, an acknowledgement).
// Redesign 2026-10-12 (CP 294 Sections 2-3): the sound follows the super admin's rules, the user's
// critical volume and the server's clock, plays from one tab per browser, and "Quiet 2 min" is shared.
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { useQuery } from '@tanstack/react-query'
import { eventsApi } from '@/api/endpoints'
import { queryKeys, queryOptions } from '@/config/queryConfig'
import {
  CRITICAL_SOUND_SECONDS, isAlertSoundReady, playCriticalSound, prepareCriticalSound, stopCriticalSound, subscribeAlertSound,
} from '@/lib/alertSound'
import { isOpenCritical } from '@/lib/criticalEvents'
import { setCriticalSounding } from '@/lib/criticalSoundState'
import {
  clearQuietIfOlderThan, getLastPlayed, getLastSoundedId, getQuiet, isSoundLeader, setLastPlayed,
  setLastSoundedId, setQuiet, startLeadership, subscribeShared,
} from '@/lib/soundLeader'
import { clampCriticalVolume, useDisplayPrefs } from '@/store/displayPrefsStore'
import { useCriticalSoundSettings } from './useCriticalSoundSettings'
import type { CriticalSoundSettings, Event, PaginatedResponse } from '@/types'

/** "Quiet 2 min": every tab of this browser. */
export const QUIET_MS = 120_000
/** A synthesized sound lasts up to ~1.7 s: a new arrival sooner than this after the last play waits for the next
 *  tick. (#338: a recorded sound waits for its own end instead — criticalPlayDue.) */
export const MIN_GAP_MS = 1_800
const TICK_MS = 1_000
/** A shared time further ahead than this comes from a computer clock that moved back: it is ignored. */
const CLOCK_SLACK_MS = 5_000
/** One page is enough for the bar and the pinned rows; acknowledge-all takes up to 200 ids. */
export const OPEN_CRITICAL_PAGE_SIZE = 50

const NO_EVENTS: Event[] = []

const newestFirst = (a: Event, b: Event) => Date.parse(b.occurred_at) - Date.parse(a.occurred_at)

export interface OpenCriticals {
  /** The open ones on the page (up to 50), newest first: the pinned rows, the bar's detail, the sound. */
  open: Event[]
  /** How many are open in all (the API's count): the bar and the tab title. */
  total: number
}

// Module level, so React Query keeps the selected value's identity while the data is unchanged. Rows
// acknowledged in this tab (optimistic) drop out — and out of the total — before the refetch confirms it.
const selectOpen = (page: PaginatedResponse<Event>): OpenCriticals => {
  const open = page.results.filter(isOpenCritical).sort(newestFirst)
  const acknowledgedHere = page.results.length - open.length
  return { open, total: Math.max(open.length, (page.count ?? open.length) - acknowledgedHere) }
}

/** The open (unacknowledged) critical events this user can see, newest first, and how many are open in
 *  all. Superusers: every group's. The sound controller also needs `dataUpdatedAt` (the fetch time). */
export function useOpenCriticals() {
  return useQuery({
    queryKey: queryKeys.events.criticalOpen(),
    queryFn: () => eventsApi.getAll({ critical: 'open', page_size: OPEN_CRITICAL_PAGE_SIZE }),
    select: selectOpen,
    ...queryOptions.events.criticalOpen,
  })
}

/**
 * When an open critical event arrived, on this computer's clock but measured with the server's: the
 * moment the list was fetched minus the API's age of the event (CP 294 edge 12: a wrong computer clock
 * does not matter, and it does not drift between polls). An old API without the age: created_at, never
 * later than now (Review Focus 3: a created_at ahead of the computer's clock counts as now, so it still
 * stops). One seeded from the WebSocket (no age yet): when it was seeded.
 */
function arrivalOf(e: Event, fetchedAt: number, now: number): number {
  if (e._seeded) return now
  const age = e.critical_age_seconds
  if (typeof age === 'number' && Number.isFinite(age) && age >= 0 && fetchedAt > 0) {
    return Math.min(fetchedAt - age * 1000, now)
  }
  const created = Date.parse(e.created_at)
  return Number.isNaN(created) ? now : Math.min(created, now)
}

/** #338 review fix round 1 — the repeat of a recorded sound is due this much early. The controller checks once a
 *  second, so without it "5.06 s clip + 5 s" (due at 10.06 s) would wait for the check at 11 s: 5.94 s of
 *  silence. With it the check at 10 s plays: 4.94 s, about the 5 s the super admin chose. Not applied to a new
 *  arrival's wait (never over the clip) nor to the synthesized sounds (unchanged). */
export const RECORDED_DUE_TOLERANCE_MS = 100

/**
 * #338 — whether the next critical play is due, `sinceMs` after the previous play STARTED (in any tab).
 * `notSounded` = an open alert inside its window that no tab has sounded yet (a new arrival).
 * A RECORDED sound (the client's recording, 5.06 s) counts from the END of the previous play — the owner's choice
 * 2026-10-13 ("ok lets proceed with your recommendation", option A): a repeat is due `repeat_seconds` after the
 * clip ended, and a new arrival waits until the clip has ended, so two copies never play over each other.
 * The synthesized sounds (all ≤ 1.7 s) keep the rule they had: a repeat every `repeat_seconds` start to start, a
 * new arrival 1.8 s after the last play. `repeat_seconds` 0 = once only, for every sound. The controller checks
 * once a second: a play happens at the first check at or after it is due (a recording's repeat: 100 ms early,
 * RECORDED_DUE_TOLERANCE_MS).
 */
export function criticalPlayDue(
  rules: Pick<CriticalSoundSettings, 'sound' | 'repeat_seconds'>, notSounded: boolean, sinceMs: number,
): boolean {
  const clipMs = rules.sound === 'client' ? CRITICAL_SOUND_SECONDS.client * 1000 : 0
  if (notSounded) return sinceMs >= Math.max(MIN_GAP_MS, clipMs)
  const toleranceMs = clipMs > 0 ? RECORDED_DUE_TOLERANCE_MS : 0
  return rules.repeat_seconds > 0 && sinceMs >= rules.repeat_seconds * 1000 + clipMs - toleranceMs
}

/** The quiet time that applies now (one far in the future = a clock that moved back: ignored). */
function quietUntilNow(now: number): number {
  const { until } = getQuiet()
  return until > now && until - now <= QUIET_MS + CLOCK_SLACK_MS ? until : 0
}

export interface CriticalSoundControl {
  /** "Quiet 2 min": quiets every tab of this browser and stops the sound playing now. */
  quiet: () => void
  /** Whole seconds of quiet left (0 = not quiet). */
  quietLeft: number
  /** An open critical exists and the browser still blocks the sound ("Click to enable sound"). */
  soundBlocked: boolean
  /** An open critical inside its sound window and not quieted (the normal chime waits meanwhile). */
  sounding: boolean
  /** Whether this alert is still inside its own sound window (popups appear only then). */
  insideWindow: (e: Event) => boolean
}

/**
 * The critical sound's controller. Call it ONCE per tab (it holds the tab's sound leadership): the
 * critical alerts controller in the layout. `open` = the open critical events, `loaded` = the list has
 * loaded once and the super admin's rules have been read (or failed) — until then the list is ignored, so the
 * default rules never sound an alert the real ones have ended — `fetchedAt` = the query's dataUpdatedAt.
 *
 * Rules (super admin's settings, defaults the client's recording / 10 s / 5 min, #338): a new open critical
 * inside its window plays at once (not within 1.8 s of the last play in any tab; a recorded sound: not before
 * the clip has ended) and ends the quiet time if it arrived after the quiet was set; then the sound repeats
 * every `repeat_seconds` (0 = once only; a recorded sound: counted from the end of the clip) while any is
 * open, inside the window (newest arrival + `stop_after_seconds`; 0 = until acknowledged) and not
 * quieted. Only the sound leader tab plays, at the user's critical volume (never below 30 %). "New" is
 * decided by event ids, not clocks: an open alert inside its window with an id above the newest one any
 * tab has sounded (shared) is not sounded yet — so an arrival missed while this tab was not the leader or
 * blocked plays when it becomes so, and one another tab already sounded never plays again. The sound
 * stops at once when none is open, and when this controller unmounts.
 */
export function useCriticalSound(openList: Event[], loaded: boolean, fetchedAt: number): CriticalSoundControl {
  const open = loaded ? openList : NO_EVENTS
  const settings = useCriticalSoundSettings()
  const criticalVolume = useDisplayPrefs((s) => s.criticalVolume)
  const leader = useSyncExternalStore(subscribeShared, isSoundLeader)
  const ready = useSyncExternalStore(subscribeAlertSound, isAlertSoundReady)
  const quietUntil = useSyncExternalStore(subscribeShared, () => getQuiet().until)
  const [sounding, setSounding] = useState(false)
  const [, setTick] = useState(0)

  const latest = useRef<{ open: Event[]; fetchedAt: number; settings: CriticalSoundSettings; criticalVolume: number }>(
    { open, fetchedAt, settings, criticalVolume })
  latest.current = { open, fetchedAt, settings, criticalVolume }
  const arrivals = useRef(new Map<number, number>())
  const soundingRef = useRef(false)
  const quietShown = useRef(0)
  const wasOpen = useRef(false)

  // This tab may play only while it holds the leadership (and only while its sound is unlocked).
  useEffect(() => startLeadership({ isUnlocked: isAlertSoundReady, subscribe: subscribeAlertSound }), [])

  const arrival = useCallback((e: Event, now: number): number =>
    arrivals.current.get(e.id) ?? arrivalOf(e, latest.current.fetchedAt, now), [])

  const windowEnd = useCallback((now: number): number => {
    const { open: current, settings: rules } = latest.current
    if (current.length === 0) return -Infinity
    if (rules.stop_after_seconds === 0) return Infinity
    return Math.max(...current.map((e) => arrival(e, now))) + rules.stop_after_seconds * 1000
  }, [arrival])

  const refreshSounding = useCallback((now: number) => {
    const on = latest.current.open.length > 0 && now < windowEnd(now) && quietUntilNow(now) === 0
    setCriticalSounding(on)
    if (soundingRef.current !== on) {
      soundingRef.current = on
      setSounding(on)
    }
  }, [windowEnd])

  /** Play if this is the moment: an alert not sounded yet (at once, 1.8 s after the last play in any tab),
   *  else the repeat. */
  const tryPlay = useCallback((now: number) => {
    const { open: current, settings: rules, criticalVolume: volume } = latest.current
    if (current.length === 0 || !isSoundLeader() || quietUntilNow(now) !== 0) return
    if (now >= windowEnd(now)) return
    const stopMs = rules.stop_after_seconds * 1000
    let newestInside = 0
    for (const e of current) {
      if (e.id > newestInside && (stopMs === 0 || now < arrival(e, now) + stopMs)) newestInside = e.id
    }
    const notSounded = newestInside > getLastSoundedId()
    let last = getLastPlayed()
    if (last > now + CLOCK_SLACK_MS) last = -Infinity
    if (!criticalPlayDue(rules, notSounded, now - last)) return
    if (playCriticalSound(rules.sound, clampCriticalVolume(volume) / 100)) {
      setLastPlayed(now)
      setLastSoundedId(newestInside)
    }
  }, [windowEnd, arrival])

  // New open criticals: fix each one's arrival the first time it is seen, end the quiet time, play.
  const ids = open.map((e) => e.id).join(',')
  useEffect(() => {
    if (!loaded) return
    const now = Date.now()
    const { open: current, fetchedAt: at, settings: rules } = latest.current
    let newest: number | null = null
    for (const e of current) {
      if (arrivals.current.has(e.id)) continue
      const t = arrivalOf(e, at, now)
      arrivals.current.set(e.id, t)
      const inside = rules.stop_after_seconds === 0 || now < t + rules.stop_after_seconds * 1000
      if (inside && (newest === null || t > newest)) newest = t
    }
    if (newest !== null) {
      clearQuietIfOlderThan(newest)
      tryPlay(now)
    }
    refreshSounding(now)
  }, [ids, loaded, tryPlay, refreshSounding])

  // #338 — the client's recording is a file: ask for it as soon as the rules say so (the defaults say so while the
  // rules load), even while the browser still blocks sound, and again when the sound unlocks in case an earlier
  // load failed. The first critical alert then does not wait for a download (a play before it is ready falls back
  // to the Hazard pattern). The load is shared and cached: asking again costs nothing.
  useEffect(() => {
    if (settings.sound === 'client') void prepareCriticalSound('client')
  }, [settings.sound, ready])

  // Became the leader, or the sound was unlocked: a missed arrival plays now.
  useEffect(() => {
    if (leader && ready) tryPlay(Date.now())
  }, [leader, ready, tryPlay])

  // The quiet time or the rules changed (here or in another tab): sounding follows, and a quiet time set
  // in any tab cuts the sound this tab is playing now.
  useEffect(() => {
    const now = Date.now()
    if (quietUntilNow(now) !== 0) stopCriticalSound()
    refreshSounding(now)
  }, [quietUntil, settings, refreshSounding])

  const anyOpen = open.length > 0
  useEffect(() => {
    if (!anyOpen) {
      // none open any more: the sound stops at once (acknowledged, removed, or the list cleared on logout)
      if (wasOpen.current) {
        wasOpen.current = false
        refreshSounding(Date.now())
        stopCriticalSound()
      }
      return
    }
    wasOpen.current = true
    const timer = setInterval(() => {
      const now = Date.now()
      tryPlay(now)
      refreshSounding(now)
      // the countdown on the bar's button, and its last step to "Quiet 2 min"
      if (quietUntilNow(now) !== 0 || quietShown.current > 0) setTick((n) => n + 1)
    }, TICK_MS)
    return () => clearInterval(timer)
  }, [anyOpen, tryPlay, refreshSounding])

  // Unmount (logout leaves the layout): silence, and nothing counts as sounding.
  useEffect(() => () => {
    setCriticalSounding(false)
    stopCriticalSound()
  }, [])

  const quiet = useCallback(() => {
    setQuiet(Date.now() + QUIET_MS)
    stopCriticalSound()
  }, [])

  const stopAfter = settings.stop_after_seconds
  const insideWindow = useCallback((e: Event) => {
    if (stopAfter === 0) return true
    const now = Date.now()
    return now < arrival(e, now) + stopAfter * 1000
  }, [stopAfter, arrival])

  const now = Date.now()
  const until = quietUntil > 0 ? quietUntilNow(now) : 0
  const quietLeft = until > 0 ? Math.max(0, Math.ceil((until - now) / 1000)) : 0
  quietShown.current = quietLeft
  return { quiet, quietLeft, soundBlocked: anyOpen && !ready, sounding, insideWindow }
}

/** The browser tab reads "CRITICAL (N) · DeterCam" while N > 0 (steady, not flashing); the title it had
 *  before comes back when none is left. */
export function useCriticalTabTitle(count: number, title: string) {
  const saved = useRef<string | null>(null)
  useEffect(() => {
    if (count > 0) {
      if (saved.current === null) saved.current = document.title
      document.title = title
    } else if (saved.current !== null) {
      document.title = saved.current
      saved.current = null
    }
  }, [count, title])
  useEffect(() => () => {
    if (saved.current !== null) {
      document.title = saved.current
      saved.current = null
    }
  }, [])
}
